import { TypedDocumentNode, gql } from '@apollo/client';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { CardBack } from '@sorare/core/src/components/card/Back';
import { Cost } from '@sorare/core/src/components/player/Cost';
import { draft, glossary } from '@sorare/core/src/lib/glossary';
import { stroke } from '@sorare/core/src/style/utils';

import { InjuryStatusIcon } from '@sorare/us-sports/src/components/player/PlayerInjury/InjuryStatusIcon';

import {
  CardToReceive_NBACommonCardSample,
  CardToReceive_NBAPlayer,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical).attrs({ center: true })`
  justify-items: center;
  &.dismissed {
    opacity: 0.5;
  }
`;

const PickCardButton = styled(ButtonBase)`
  transition: transform 0.1s ease-out;
  aspect-ratio: var(--card-aspect-ratio);
  max-width: 200px;
  position: relative;
  cursor: unset;

  &.selectable {
    cursor: pointer;
    &:hover,
    &:focus {
      opacity: 1;
      transform: scale(1.01);
    }
  }
  &.selected,
  &.selected img {
    ${stroke()}
  }
`;

const CardOption = styled.img`
  width: 100%;
`;

const SlotWrapper = styled(animated.div)`
  position: absolute;
  inset: 0;
  transform-style: preserve-3d;
  backface-visibility: hidden;
  width: 100%;
  max-width: 200px;
  aspect-ratio: var(--card-aspect-ratio);
`;

const Side = styled.div`
  mask-size: cover;
  backface-visibility: hidden;
  position: absolute;
  inset: 0;
  overflow: hidden;
  &:nth-child(2) {
    transform: rotateY(180deg);
  }
`;

const Img = styled.img`
  width: 100%;
  max-width: 200px;
`;

const Details = styled(animated.div)`
  display: flex;
  align-items: center;
  gap: var(--double-unit);
  font-size: 12px;
`;

const TwoFacesCard = ({
  front,
  back,
  style,
}: {
  front: string;
  back: ReactNode;
  style: any;
}) => {
  return (
    <SlotWrapper style={style}>
      <Side>{back}</Side>
      <Side>
        <Img src={front} alt="" />
      </Side>
    </SlotWrapper>
  );
};

type Props = {
  card: CardToReceive_NBACommonCardSample;
  isSelectable?: boolean;
  isSelected?: boolean;
  isDismissed?: boolean;
  onSelect?: () => any;
  onDisplayPlayerInfo?: () => any;
  flipped?: boolean;
  reveal?: boolean;
};

export const CardToReceive = ({
  card,
  isSelectable = true,
  isSelected = false,
  isDismissed = false,
  onSelect,
  onDisplayPlayerInfo,
  flipped = false,
  reveal = false,
}: Props) => {
  const { formatMessage } = useIntl();
  const buttonProps = isSelectable ? { tabIndex: 0 } : { tabIndex: -1 };

  const flipCard = useSpring({
    from: {
      rotateY: 0,
    },
    to: {
      rotateY: reveal ? 180 : 0,
    },
  });
  const slideScore = useSpring({
    from: {
      y: -10,
      opacity: 0,
    },
    to: {
      y: reveal ? 0 : -10,
      opacity: reveal ? 1 : 0,
    },
    delay: 100,
  });

  const cardImageUrl = proxyUrl(card.commonCardImageUrl, {
    cropWidth: 320,
  });

  return (
    <Wrapper
      className={classNames({
        dismissed: isDismissed,
      })}
    >
      <PickCardButton
        {...buttonProps}
        className={classNames({
          selectable: isSelectable,
          selected: isSelected,
          dismissed: isDismissed,
        })}
        type="button"
        title={
          isSelectable
            ? formatMessage(
                {
                  id: 'CardSwap.CardToReceive.title',
                  defaultMessage: 'Pick {name}',
                },
                { name: card.player.displayName }
              )
            : card.player.displayName
        }
        onClick={onSelect}
      >
        <CardOption
          src={cardImageUrl}
          style={{ opacity: flipped ? 0 : 1 }}
          alt=""
        />
        {flipped && (
          <TwoFacesCard
            style={flipCard}
            front={cardImageUrl}
            back={<CardBack sport={Sport.NBA} rarity={Rarity.common} />}
          />
        )}
      </PickCardButton>
      <Details style={flipped ? slideScore : undefined}>
        <Cost
          score={card.player.tenGameAverageGameStats.score}
          title={formatMessage(draft.nbaTenGameAverageScoreTitle, {
            score: card.player.tenGameAverageGameStats.score,
          })}
        />
        <Horizontal>
          {onDisplayPlayerInfo && (
            <IconButton
              disableDebounce
              small
              color="quaternary"
              onClick={e => {
                e.stopPropagation();
                onDisplayPlayerInfo();
              }}
              title={formatMessage(glossary.showPlayerInfo)}
              icon={faInfoCircle}
            />
          )}
          {(!card.player.isActive || card.player.playerInjury) && (
            <InjuryStatusIcon />
          )}
        </Horizontal>
      </Details>
    </Wrapper>
  );
};

const PLAYER_FRAGMENT = gql`
  fragment CardToReceive_NBAPlayer on NBAPlayer {
    id
    slug
    displayName
    isActive
    playerInjury {
      status
    }
    tenGameAverageGameStats {
      score
    }
  }
` as TypedDocumentNode<CardToReceive_NBAPlayer>;

CardToReceive.fragments = {
  NBACommonCardSample: gql`
    fragment CardToReceive_NBACommonCardSample on NBACommonCardSample {
      id
      commonCardImageUrl
      player {
        slug
        ...CardToReceive_NBAPlayer
      }
    }
    ${PLAYER_FRAGMENT}
  ` as TypedDocumentNode<CardToReceive_NBACommonCardSample>,
};
